import { useState } from 'react';
import { Upload, X, Image as ImgIcon, Film, Loader2 } from 'lucide-react';
import api, { resolveMedia } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';

// nginx caps the request body at 512M on the terra vhosts; stay under it and
// tell the user the number rather than letting nginx answer with bare HTML.
const MAX_UPLOAD_MB = 500;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
const asMB = (bytes) => Math.max(1, Math.round(bytes / 1024 / 1024));

// Multi-image uploader with preview and drag-reorder
export function MediaUploader({ value = [], onChange, accept = 'image/*', kind = 'image', label = 'Upload Media', multiple = true }) {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const picked = Array.from(files);
    const tooBig = picked.filter((f) => f.size > MAX_UPLOAD_BYTES);
    const ok = picked.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    if (tooBig.length) {
      toast({
        title: tooBig.length > 1 ? `${tooBig.length} files are too large` : 'File too large',
        description: `${tooBig.map((f) => `"${f.name}" (${asMB(f.size)} MB)`).join(', ')} — the limit is ${MAX_UPLOAD_MB} MB per file. Please compress and try again.`,
        variant: 'destructive',
      });
    }
    if (!ok.length) return;
    setUploading(true);
    const arr = [...value];
    try {
      for (const f of ok) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        arr.push(data.url);
      }
    } catch (e) {
      // 413 comes from nginx as a bare HTML page, so say what actually went wrong.
      const tooLarge = e?.response?.status === 413;
      toast({
        title: tooLarge ? 'File too large' : 'Upload failed',
        description: tooLarge
          ? `The server refused that file. The limit is ${MAX_UPLOAD_MB} MB — please compress it and try again.`
          : 'Try a smaller file or a different format.',
        variant: 'destructive',
      });
    } finally {
      // Keep whatever did upload: a failure part-way through a batch must not
      // drop the earlier files, which are already on the server.
      if (arr.length > value.length) onChange(multiple ? arr : [arr[arr.length - 1]]);
      setUploading(false);
    }
  };

  const remove = async (idx) => {
    const url = value[idx];
    try { await api.delete('/admin/upload', { params: { url } }); } catch (e) { /* ignore */ }
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= value.length) return;
    const arr = [...value];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    onChange(arr);
  };

  return (
    <div>
      <label className="text-sm font-medium text-neutral-700 mb-2 block">{label}</label>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-neutral-200 bg-neutral-100">
            {kind === 'video' || url.match(/\.(mp4|mov|webm|m4v)$/i) ? (
              <video src={url ? resolveMedia(url) + '#t=0.1' : ''} preload="metadata" muted playsInline className="w-full h-full object-cover" />
            ) : (
              <img src={resolveMedia(url)} alt={'media ' + i} className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <button type="button" onClick={() => remove(i)} className="px-2 py-1 bg-red-600 text-white text-xs rounded">Remove</button>
            </div>
            {multiple && (
              <div className="absolute bottom-1 left-1 flex gap-1">
                <button type="button" onClick={() => move(i, -1)} className="px-1.5 py-0.5 bg-white/90 text-xs rounded">←</button>
                <button type="button" onClick={() => move(i, 1)} className="px-1.5 py-0.5 bg-white/90 text-xs rounded">→</button>
              </div>
            )}
            {i === 0 && multiple && <span className="absolute top-1 left-1 px-2 py-0.5 bg-[#DC2626] text-white text-xs rounded">Cover</span>}
          </div>
        ))}
        <label className="aspect-square rounded-lg border-2 border-dashed border-neutral-300 hover:border-[#DC2626] cursor-pointer flex flex-col items-center justify-center text-neutral-500 hover:text-[#DC2626] transition-colors">
          <input type="file" accept={accept} multiple={multiple} className="hidden" onChange={(e) => handleFiles(e.target.files)} />
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : (kind === 'video' ? <Film className="w-7 h-7" /> : <ImgIcon className="w-7 h-7" />)}
          <span className="text-xs mt-1">{uploading ? 'Uploading...' : 'Upload'}</span>
        </label>
      </div>
      <p className="mt-2 text-xs text-neutral-500">Files are uploaded directly to your server. Max {MAX_UPLOAD_MB} MB per file. {multiple ? 'Drag-reorder using arrows. The first image is used as cover.' : 'Replace by uploading new.'}</p>
    </div>
  );
}

export function SingleUploader({ value, onChange, accept = 'image/*', kind = 'image', label = 'Upload' }) {
  const arr = value ? [value] : [];
  return (
    <MediaUploader value={arr} onChange={(v) => onChange(v[0] || '')} accept={accept} kind={kind} label={label} multiple={false} />
  );
}
